import React, { useState, useRef, useEffect } from 'react';
import proptypes from 'prop-types';
const { string, bool, object, func } = proptypes;
import { StoreMap } from '@thd-olt-component-react/store-map';
import {
  Curbside,
  Catalog,
  Hammer
} from '@one-thd/sui-icons';
import {
  Card,
  CardBody,
  Typography,
  Button
} from '@one-thd/sui-atomic-components';
import {
  StorePod,
  StorePodAddress,
  StorePodHours,
  StorePodName,
  StorePodPhoneNumber,
  StorePodOpenAndClose
} from '@thd-olt-component-react/store-pod';
import {
  StoreSearch,
  SearchInput,
  Results
} from '@thd-olt-component-react/store-search';

import { extend, useDataModel } from '@thd-nucleus/data-sources';

import zipCodeLocalizer from '../utils/zip-code-localizer';

const MyStore = ({ storeId = '0121', hostName = '', hideCurbside = false, customStoreHandler = () => {} }) => {
  const [searchInput, setSearchInput] = useState(storeId);
  const [searchCoordinates, setSearchCoordinates] = useState(null);
  const { data } = useDataModel('storeSearch', {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1',
    },
  });

  const { stores } = data?.storeSearch || {};
  const store = stores?.[0];
  const localizerRef = useRef(null);

  const MAP_OTIONS = {
    disableBirdseye: true,
    disableInfoBox: true,
    height: '150px',
    disableScrollWheelZoom: true
  };

  let STORE_LINKS = [
    {
      icon: <Curbside />,
      label: 'Curbside',
      href: `${hostName}/c/curbside_pickup`
    },
    {
      icon: <Catalog />,
      label: 'Local Ad',
      href: `${hostName}/c/localad`
    },
    {
      icon: <Hammer />,
      label: 'Workshops',
      href: `${hostName}/workshops/`
    }
  ];

  if (hideCurbside) {
    STORE_LINKS = STORE_LINKS.filter((link) => link.label !== 'Curbside');
  }

  const onSuccessfulUpdate = () => {
    zipCodeLocalizer.update(localizerRef.current, searchInput);
  };

  const handleTileClick = (link) => {
    global.LIFE_CYCLE_EVENT_BUS.trigger(
      'header-my-store.header-click',
      { clickPath: (`my store>${link?.label.toLowerCase()}`) }
    );
    if (window?.location && link?.href) {
      window.location.assign(link?.href);
    }
  };

  const focusFirstStore = () => {
    // eslint-disable-next-line
    document?.getElementById('podCardId1')?.focus();
  };

  const storePodLocalizeCallback = () => {
    zipCodeLocalizer.updateDeliveryZipCode(localizerRef, searchInput, onSuccessfulUpdate);
  };

  useEffect(() => {
    localizerRef.current = window?.THD_LOCALIZER_AUTO_INIT?.Localizer;
  }, []);

  return (
    <div data-component="MyStore">
      <div className="sui-pb-2 sui-pt-2 sui-pl-2 sui-pr-3">
        <StoreMap
          mapOptions={MAP_OTIONS}
          stores={[store]}
          storeMapId="myStoreMap"
        />
      </div>
      <div className="sui-overflow-y-auto">
        <StorePod store={store}>
          <div className="sui-p-4">
            <div className="sui-pb-1">
              <StorePodOpenAndClose />
            </div>
            <div className="sui-pb-3">
              <StorePodName hostName={hostName} />
            </div>
            <div className="sui-flex">
              <div className="sui-w-4/6">
                <span className="sui-font-bold">Address</span>
                <div className="sui-pb-2">
                  <StorePodAddress />
                </div>
                <div className="sui-pb-2">
                  <StorePodPhoneNumber boldLabel displayMultiline type="store" />
                </div>
                <div className="sui-pb-2">
                  <StorePodPhoneNumber boldLabel displayMultiline type="pro" />
                </div>
              </div>
              <div className="sui-w-2/6">
                <span className="sui-font-bold">Hours</span>
                <StorePodHours displayMultiline />
              </div>
            </div>
          </div>
        </StorePod>
        <div className="sui-gap-4 sui-grid sui-grid-cols-3 sui-p-4">
          {STORE_LINKS.map((link) => (
            <Card key={link?.label} onClick={() => handleTileClick(link)}>
              <CardBody>
                <div
                  alt={link.label}
                  className="sui-flex sui-flex-col sui-items-center sui-justify-center sui-p-2"
                >
                  {link.icon}
                  <span className="sui-mt-2 sui-text-center">
                    <Typography>{link.label}</Typography>
                  </span>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
        <div className="sui-w-full">
          <StoreSearch
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            searchCoordinates={searchCoordinates}
            setSearchCoordinates={setSearchCoordinates}
            searchSuccessCallback={focusFirstStore}
            storePodLocalizeCallback={storePodLocalizeCallback}
          >
            <div className="sui-w-full">
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
              <div className="sui-border-solid sui-border-t-4 sui-border-b-4 sui-border-[#F4F4F4] sui-pl-4 sui-pr-4">
                <div className="sui-pt-2 sui-pb-2">
                  <Typography variant="h5">
                    Find Another Store
                  </Typography>
                </div>
                <SearchInput />
              </div>
              <div className="sui-pt-3">
                <Results
                  omitStoreNumber={storeId}
                  disableScroll
                  maxNumberOfResults={5}
                  hostName={hostName}
                  customStoreHandler={customStoreHandler}
                />
              </div>
              <div className="sui-bg-inactive sui-w-full sui-h-16">
                <div className="sui-flex sui-justify-center">
                  <Button variant="text" href={`${hostName}/l/store-locator`}>Store Locator</Button>
                </div>
              </div>
            </div>
          </StoreSearch>
        </div>
      </div>
    </div>
  );
};

MyStore.propTypes = {
  storeId: string,
  hostName: string,
  hideCurbside: bool,
  customStoreHandler: func,
};

MyStore.displayName = 'MyStore';

MyStore.dataModel = extend(StoreSearch);

export { MyStore };
